/**
 * Centralized keyboard shortcuts configuration.
 *
 * This file serves as the single source of truth for all keyboard shortcuts used
 * throughout the application. It helps prevent conflicts and makes it easier to
 * manage and document shortcuts.
 *
 * Usage:
 * - Import the specific shortcut constant you need
 * - Use with useKeyPress, useHotkeys, or getHotkeyHandler
 * - Add new shortcuts here to ensure no conflicts
 */

// ============================================================================
// GLOBAL SHORTCUTS (work across the entire application)
// ============================================================================

export const GLOBAL_SHORTCUTS = {
	// Sidebar toggles
	// Includes both plain key and modifier versions to prevent browser navigation
	LEFT_SIDEBAR_TOGGLE: ['openbracket', 'meta.openbracket', 'ctrl.openbracket'],
	RIGHT_SIDEBAR_TOGGLE: [
		'closebracket',
		'meta.closebracket',
		'ctrl.closebracket',
	],

	// Command palette and search
	COMMAND_PALETTE: ['mod+k'], // Cmd/Ctrl + K
	SEARCH_PALETTE: ['/'], // /
	SPOTLIGHT_PALETTE: ['shift.p'], // Shift + P

	// Theme and UI
	TOGGLE_COLOR_SCHEME: ['mod+J'], // Cmd/Ctrl + J

	// Create menu
	CREATE_MENU: ['C'], // C key

	// AI
	NEW_AI_CHAT: ['mod+shift+o'], // Cmd/Ctrl + Shift + O

	// Navigation
	ESCAPE: ['esc'], // Escape key
};

// ============================================================================
// CONTEXT-SPECIFIC SHORTCUTS (work in specific components/contexts)
// ============================================================================

export const TABLE_SHORTCUTS = {
	// Selection shortcuts
	SELECT_ALL: ['meta.a'], // Cmd/Ctrl + A
	DESELECT_ALL: ['esc'], // Escape
	TOGGLE_SELECTION: ['x'], // X key

	// Entity actions
	OPEN_ENTITY_DRAWER: ['space'], // Space (toggles open/close)
	DELETE_ITEM: ['delete'], // Delete key
	OPEN_ACTIONS: ['meta.k'], // Cmd/Ctrl + K (for table actions)
};

export const EDITOR_SHORTCUTS = {
	// Text formatting (handled by editor, documented for reference)
	BOLD: ['mod+b'], // Cmd/Ctrl + B
	ITALIC: ['mod+i'], // Cmd/Ctrl + I
	UNDERLINE: ['mod+u'], // Cmd/Ctrl + U

	// Editor actions
	SAVE: ['mod+s'], // Cmd/Ctrl + S
	CANCEL: ['esc'], // Escape
};

export const FORM_SHORTCUTS = {
	// Form navigation
	SUBMIT: ['enter'], // Enter
	SUBMIT_WITH_MOD: ['mod+Enter'], // Cmd/Ctrl + Enter (for comments, replies)
	CANCEL: ['esc'], // Escape
};

export const AUTOMATION_SHORTCUTS = {
	// Automation execution
	RUN_CTRL: ['ctrl.Enter'], // Ctrl + Enter
	RUN_META: ['meta.Enter'], // Cmd + Enter
	RUN_SHIFT: ['shift.Enter'], // Shift + Enter

	// Edit block shortcuts
	TOGGLE_EDIT_ACTION: ['F'], // F key for toggling edit actions
};

export const NAVIGATION_SHORTCUTS = {
	// Arrow key navigation
	ARROW_UP: ['uparrow'],
	ARROW_DOWN: ['downarrow'],
	ARROW_LEFT: ['leftarrow'],
	ARROW_RIGHT: ['rightarrow'],

	// Selection with arrows
	SELECT_PREVIOUS: ['uparrow'],
	SELECT_NEXT: ['downarrow'],
	CONFIRM_SELECTION: ['enter'],

	// Alternative navigation (for search/command palettes)
	NEXT_ALT: ['ctrl.n'], // Ctrl + N (alternative to down arrow)
	PREV_ALT: ['ctrl.p'], // Ctrl + P (alternative to up arrow)
	TAB_COMPLETE: ['Tab'], // Tab for autocomplete

	// Find and replace navigation
	FIND_PREVIOUS: ['Shift+Enter'], // Shift + Enter for find previous
};

export const FILTER_SHORTCUTS = {
	// Filter-specific shortcuts
	TOGGLE_FILTER: ['F'], // F key for toggling filters
	CLOSE_FILTER: ['esc'], // Escape to close filter
};

// ============================================================================
// SHORTCUT GROUPS (for easy reference and conflict checking)
// ============================================================================

/**
 * All shortcuts used across the application.
 * Useful for conflict detection and documentation.
 */
export const ALL_SHORTCUTS = {
	...GLOBAL_SHORTCUTS,
	...TABLE_SHORTCUTS,
	...EDITOR_SHORTCUTS,
	...FORM_SHORTCUTS,
	...AUTOMATION_SHORTCUTS,
	...NAVIGATION_SHORTCUTS,
	...FILTER_SHORTCUTS,
};

/**
 * Shortcuts that should work globally (not context-specific)
 */
export const GLOBAL_SHORTCUT_KEYS = [
	...GLOBAL_SHORTCUTS.LEFT_SIDEBAR_TOGGLE,
	...GLOBAL_SHORTCUTS.RIGHT_SIDEBAR_TOGGLE,
	...GLOBAL_SHORTCUTS.COMMAND_PALETTE,
	...GLOBAL_SHORTCUTS.SEARCH_PALETTE,
];

/**
 * Keys that are commonly used and should be carefully managed to avoid conflicts
 */
export const RESERVED_KEYS = [
	'mod+k', // Command palette
	'mod+s', // Save (context-dependent)
	'mod+a', // Select all (context-dependent)
	'/', // Search
	'[', // Left sidebar toggle
	']', // Right sidebar toggle
	'esc', // Cancel/escape (context-dependent)
	'enter', // Confirm/submit (context-dependent)
];

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Check if a key combination is already in use
 */
export function isShortcutInUse(shortcut: string): boolean {
	const allShortcutValues = Object.values(ALL_SHORTCUTS).flat();
	return allShortcutValues.includes(shortcut);
}

/**
 * Get all shortcuts for a specific context
 */
export function getShortcutsForContext(context: keyof typeof ALL_SHORTCUTS) {
	return ALL_SHORTCUTS[context];
}

/**
 * Format shortcut for display (convert internal format to user-friendly format)
 */
export function formatShortcutForDisplay(shortcut: string): string {
	return shortcut
		.replace('mod+', navigator.platform.includes('Mac') ? '⌘' : 'Ctrl+')
		.replace('meta+', '⌘')
		.replace('ctrl+', 'Ctrl+')
		.replace('shift+', 'Shift+')
		.replace('alt+', 'Alt+')
		.replace('openbracket', '[')
		.replace('closebracket', ']')
		.replace('uparrow', '↑')
		.replace('downarrow', '↓')
		.replace('leftarrow', '←')
		.replace('rightarrow', '→')
		.replace('esc', 'Escape')
		.replace('enter', 'Enter');
}

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type ShortcutKey =
	(typeof ALL_SHORTCUTS)[keyof typeof ALL_SHORTCUTS][number];
export type GlobalShortcutKey =
	(typeof GLOBAL_SHORTCUTS)[keyof typeof GLOBAL_SHORTCUTS][number];
export type TableShortcutKey =
	(typeof TABLE_SHORTCUTS)[keyof typeof TABLE_SHORTCUTS][number];
export type EditorShortcutKey =
	(typeof EDITOR_SHORTCUTS)[keyof typeof EDITOR_SHORTCUTS][number];
export type FormShortcutKey =
	(typeof FORM_SHORTCUTS)[keyof typeof FORM_SHORTCUTS][number];
export type NavigationShortcutKey =
	(typeof NAVIGATION_SHORTCUTS)[keyof typeof NAVIGATION_SHORTCUTS][number];
